import { createFileRoute, redirect } from '@tanstack/react-router';

import { client } from '@@api/client';
import { handleLoaderError } from '@@router/handleLoaderError';

import UnityArticleRevision from '../../-components/articleViews/UnityArticle/UnityArticleRevision';

export const Route = createFileRoute('/_authenticated/articles/$id/revisions/$revisionId')({
    context: ({ params }) => ({
        revisionsQuery: client.content.revisions.getAll.queryOptions(
            { params: { id: params.id } },
            { refetchOnWindowFocus: false },
        ),
        // The page compares the selected revision with the latest one, whose id comes from the list
        revisionQuery: (revisionId: string) =>
            client.content.revisions.get.queryOptions(
                { params: { articleId: params.id, revisionId } },
                { refetchOnWindowFocus: false },
            ),
        diffQuery: client.content.articleDiff.get.queryOptions(
            {
                params: { id: params.id },
                query: { origin: 'MASTER', derivative: params.revisionId },
            },
            { refetchOnWindowFocus: false },
        ),
    }),
    loader: async ({
        context: { queryClient, revisionsQuery, revisionQuery, diffQuery },
        params,
        parentMatchPromise,
    }) => {
        // The requests go out in the article's language, which the article's loader sets
        await parentMatchPromise;

        const diffFailed = queryClient.query({ ...diffQuery, staleTime: 'static' }).then(
            () => false,
            () => true,
        );
        const [revisions] = await Promise.all([
            queryClient.query({ ...revisionsQuery, staleTime: 'static' }).catch(handleLoaderError),
            queryClient
                .query({ ...revisionQuery(params.revisionId), staleTime: 'static' })
                .catch(handleLoaderError),
        ]);

        // Some revisions can't be compared yet because of duplicate ids in their content, so the
        // article opens instead. This goes once those ids are removed from the database. An unknown
        // revision has thrown above, so it shows the 404 page.
        if (await diffFailed) {
            throw redirect({
                to: '/articles/$id',
                params: { id: params.id },
                search: true,
                replace: true,
            });
        }

        const latestRevisionId = revisions.body[0]?.id;

        if (latestRevisionId) {
            await queryClient
                .query({ ...revisionQuery(latestRevisionId), staleTime: 'static' })
                .catch(handleLoaderError);
        }
    },
    component: UnityArticleRevision,
});
